import {
    Attachment,
    Avatar,
    Button,
    ButtonGroup,
    classNames,
    FDate,
    Flexer,
    Header,
    Heading,
    Icon,
    Link,
    logger,
    Pagination,
    plural,
    Resizable,
    stopEvent,
    Text,
    useCustomEvent,
    useStorage,
    View,
} from '@fold-dev/core'
import { useEffect, useMemo, useRef, useState } from 'react'
import { TbAirBalloon, TbArchive, TbChecks, TbConfetti } from 'react-icons/tb'
import { Outlet, useNavigate, useParams } from 'react-router'
import { Block, Error, Loading } from '../components'
import { NOTIFICATION_ACTION } from '../constants'
import {
    byCreatedAt,
    getCachedProjectView,
    getDestination,
    getNotificationActionDescription,
    getNotificationText,
    scrollDown,
    useDocumentTitle,
} from '../helpers'
import { useNotifications } from '../hooks/api.hooks'
import { useAppStore } from '../store/app'
import { PresenceAvatar } from '../components/common/presence-avatar'
import { MinimizeHeader } from '../components/minimize-header'
import { InboxResizable } from './sidebar.layout'

const markAllNotificationsAsRead = (notifications) => {
    return Object.keys(notifications).reduce((result, projectId) => {
        result[projectId] = notifications[projectId].map((notification) => ({
            ...notification,
            read: true,
        }))
        return result
    }, {})
}

const markNotificationsAsRead = (notificationsByProject, notificationId) => {
    return Object.keys(notificationsByProject).reduce((result, projectId) => {
        result[projectId] = notificationsByProject[projectId].map((notification) =>
            notification.id === notificationId ? { ...notification, read: true } : notification
        )
        return result
    }, {})
}

export const Notification = (props) => {
    const { notification, onMarkAsRead, onOpen, hideAttachments, large } = props
    const {
        id,
        action,
        read,
        createdAt,
        projectId,
        projectName,
        projectColor,
        taskId,
        taskTitle,
        userId,
        userImage,
        userName,
        actorId,
        actorImage,
        actorName,
        taskComment,
        attachments,
    } = notification
    const date = useMemo(() => FDate(new Date(createdAt)).fromNow(), [createdAt])
    const text = useMemo(() => getNotificationText(notification), [notification])
    const className = classNames({
        'inbox-notification': true,
        'read': read,
    })

    return (
        <View
            column
            className={className}
            alignItems="flex-start"
            gap="1rem"
            width="100%"
            p="1rem 1.5rem"
            position="relative"
            onClick={() => onOpen(notification)}>
            {!read && (
                <View
                    position="absolute"
                    width={5}
                    height="calc(100% + 1px)"
                    style={{ left: -1, top: -1 }}
                    bgToken="accent"
                />
            )}

            <View
                row
                position="relative"
                alignItems="flex-start"
                gap="1rem"
                flex={1}>
                {actorId && (
                    <PresenceAvatar
                        userId={actorId}
                        src={actorImage}
                        name={actorName}
                    />
                )}
                <View
                    column
                    width="100%"
                    gap="0.5rem"
                    alignItems="flex-start">
                    <View
                        width="100%"
                        position="relative">
                        <span
                            className="f-text inbox-text clamp-text-one"
                            dangerouslySetInnerHTML={{ __html: getDestination(notification) }}></span>
                    </View>

                    <Text
                        className="inbox-text"
                        size="sm">
                        {text}
                    </Text>

                    <div
                        className="f-row"
                        style={{ gap: 10 }}>
                        {projectId && (
                            <Text
                                color={projectColor}
                                size="sm"
                                fontWeight={600}
                                flex={1}>
                                {projectName}
                            </Text>
                        )}
                        <Text
                            colorToken="text-weaker"
                            size="sm">
                            {date}
                        </Text>
                        {!read && (
                            <Link
                                size="sm"
                                colorToken="accent"
                                className="f-underline"
                                onClick={(e) => {
                                    stopEvent(e)
                                    onMarkAsRead(id)
                                }}>
                                Mark as read
                            </Link>
                        )}
                    </div>

                    {!!attachments && !hideAttachments && (
                        <View
                            row
                            width="100%"
                            gap="1rem"
                            wrap="wrap"
                            justifyContent="flex-start">
                            {attachments.map(({ id, type, url, filesize, name }, index) => (
                                <Attachment
                                    width={225}
                                    key={index}
                                    mime={type}
                                    filesize={filesize}
                                    label={name}
                                    onClick={(e) => {
                                        stopEvent(e)
                                        window.open(url)
                                    }}
                                    className="f-buttonize-outline"
                                />
                            ))}
                        </View>
                    )}
                </View>
            </View>
        </View>
    )
}

const key = 'FRONTEER_INBOX_RESIZABLE_WIDTH'

const Resize = ({ children, disabled }) => {
    const { getStorage, setStorage } = useStorage()
    const width = useMemo(() => +getStorage(key) || 500, [])

    const handleNavChange = (width) => {
        setStorage(key, width)
    }

    return (
        <Resizable
            column
            position="end"
            alignItems="flex-start"
            alignContent="flex-start"
            justifyContent="flex-start"
            onChange={handleNavChange}
            width={width}
            style={disabled ? { width: 'calc(100% - (var(--frame-padding)))' } : {}}
            max={600}
            min={400}
            height="calc(100% - (var(--frame-padding) * 2))"
            handle={<></>}
            railProps={{
                style: disabled ? { display: 'none' } : { '--f-resizable-color': 'transparent', 'zIndex': 2000 },
            }}
            bg="transparent">
            {children}
        </Resizable>
    )
}

export const InboxLayout = () => {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<any>(null)
    const ref = useRef(null)
    const { getStorage, setStorage } = useStorage()
    const [page, setPage] = useState(1)
    const pageSize = 50
    const { projectId, workspaceId, taskId } = useParams()
    const notificationsAPI = useNotifications()
    const navigate = useNavigate()
    const actions = useAppStore((state) => state.actions)
    const notifications = useAppStore((state) => state.notifications)
    const allNotifications = useMemo(() => {
        return Object.keys(notifications)
            .reduce((acc, val) => [...acc, ...notifications[val]], [])
            .sort(byCreatedAt)
            .reverse()
    }, [notifications])
    const hasUnread = useMemo(() => {
        return Object.values(notifications).reduce((acc, group) => {
            return acc || group.some((notification) => !notification.read)
        }, false)
    }, [notifications])
    const pagedNotifications = useMemo(() => {
        return allNotifications.filter(
            (_, index: number) => index >= pageSize * (page - 1) && index < pageSize * (page - 1) + pageSize
        )
    }, [allNotifications, pageSize, page])

    const fetchData = async () => {
        setLoading(true)
        setError(null)

        try {
            // const notifications = await notificationsAPI.getInbox(workspaceId)
            // actions.setNotifications(notifications)
            // actions.setSections([])
            setLoading(false)
        } catch (e) {
            logger(e)
            setLoading(false)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const handleMarkAllAsRead = async () => {
        setError(null)

        try {
            actions.setNotifications(markAllNotificationsAsRead(notifications))
            await notificationsAPI.updateAll(workspaceId, { read: true })
        } catch (e) {
            logger(e)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const handleMarkAsRead = async (notificationId) => {
        setError(null)

        try {
            actions.setNotifications(markNotificationsAsRead(notifications, notificationId))
            await notificationsAPI.update(workspaceId, notificationId, { read: true })
        } catch (e) {
            logger(e)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const handleRemoveAll = async () => {
        setError(null)

        try {
            actions.setNotifications({})
            await notificationsAPI.removeAll(workspaceId)
        } catch (e) {
            logger(e)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const handleOpen = async (notification) => {
        handleMarkAsRead(notification.id)
        switch (notification.action) {
            case NOTIFICATION_ACTION.PROJECT.MESSAGE_MENTION:
                const { projectId } = notification
                actions.setSidebar('inbox')
                const view = getCachedProjectView(projectId)
                navigate(`/app/workspace/${workspaceId}/c/${projectId}/${view}`)
                break
            default:
                navigate(`/app/workspace/${workspaceId}/inbox/task/${notification.taskId}`)
                break
        }
    }

    useDocumentTitle('Inbox')

    const handleScrollDown = () => {
        scrollDown(ref.current)
    }

    useCustomEvent('sidebararearoute:scroll-down', handleScrollDown)

    useEffect(() => {
        fetchData()
    }, [projectId])

    return (
        <>
            <Resize disabled={!taskId}>
                <View
                    column
                    flex={1}
                    width="100%"
                    height="calc(100% - (var(--frame-padding) * 2))"
                    position="relative"
                    justifyContent="stretch"
                    style={{
                        overflow: 'hidden',
                        borderTopLeftRadius: 'var(--frame-radius)',
                        borderTopRightRadius: 'var(--frame-radius)',
                        borderBottomLeftRadius: 'var(--frame-radius)',
                        borderBottomRightRadius: 'var(--frame-radius)',
                        border: 'var(--frame-border)',
                    }}>
                    <Error error={error} />
                    <Loading loading={loading} />
                    <MinimizeHeader>
                        <Heading as="h5">Inbox</Heading>
                        <Text
                            flex={1}
                            size="sm"
                            colorToken="text-weaker">
                            {allNotifications.length}
                        </Text>
                        <Flexer />
                        <ButtonGroup>
                            {hasUnread && (
                                <Button
                                    size="sm"
                                    onClick={handleMarkAllAsRead}
                                    prefix={<Icon icon={TbChecks} />}>
                                    Mark all as read
                                </Button>
                            )}
                            <Button
                                size="sm"
                                onClick={handleRemoveAll}
                                prefix={<Icon icon={TbArchive} />}>
                                Archive all
                            </Button>
                        </ButtonGroup>
                    </MinimizeHeader>
                    <View
                        column
                        flex={1}
                        width="100%"
                        height="100%"
                        position="relative"
                        justifyContent="stretch">
                        <View
                            width="100%"
                            height="100%"
                            style={{ top: 0, left: 0 }}
                            bgToken="surface"
                            position="absolute"
                            p="0"
                            className="f-overflow-y-auto">
                            {!pagedNotifications.length && (
                                <View
                                    column
                                    position="absolute"
                                    style={{ inset: 0 }}
                                    gap="1rem">
                                    <Icon
                                        icon={TbAirBalloon}
                                        color="var(--f-color-surface-strongest)"
                                        style={{
                                            '--f-icon-sizing-md': '7rem',
                                            '--f-icon-stroke-width-md': '0.75',
                                        }}
                                    />
                                    <Heading
                                        as="h4"
                                        textAlign="center">
                                        You're all caught up!
                                    </Heading>
                                    <Text textAlign="center">
                                        You don't have any inbox notifications.
                                    </Text>
                                </View>
                            )}

                            {pagedNotifications.map((notification, index) => (
                                <Notification
                                    key={notification.id}
                                    notification={notification}
                                    onMarkAsRead={handleMarkAsRead}
                                    onOpen={() => handleOpen(notification)}
                                    large
                                />
                            ))}

                            {allNotifications.length > pageSize && (
                                <View
                                    row
                                    p="1rem 0 1rem 0">
                                    <Pagination
                                        width="fit-content"
                                        pageSize={pageSize}
                                        totalCount={allNotifications.length}
                                        currentPage={page}
                                        siblingCount={4}
                                        separator="..."
                                        onPageChange={setPage}
                                    />
                                </View>
                            )}
                        </View>
                    </View>
                </View>
            </Resize>

            <View
                ref={ref}
                column
                flex={1}
                height="calc(100% - (var(--frame-padding) * 2))"
                m="0 var(--frame-padding)"
                position="relative"
                justifyContent="stretch"
                className="sidebar-area-route f-scrollbar"
                display={taskId ? 'flex' : 'none'}
                style={{
                    overflow: 'scroll',
                    borderTopLeftRadius: 'var(--frame-radius)',
                    borderTopRightRadius: 'var(--frame-radius)',
                    borderBottomLeftRadius: 'var(--frame-radius)',
                    borderBottomRightRadius: 'var(--frame-radius)',
                    border: 'var(--frame-border)',
                }}>
                <Outlet />
            </View>
        </>
    )
}
